import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Zap, Workflow, Play, Sparkles, Activity, Plus,
  Trash2, History, MoreVertical, GitCompare,
  Calendar, Power, Timer, Share2, DollarSign,
  Users, Mail, Link2, UserPlus, Crown, Shield,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import confetti from "canvas-confetti";
import PageTransition from "@/components/PageTransition";
import AnimatedCounter from "@/components/AnimatedCounter";
import OrchestrationTab from "@/components/OrchestrationTab";
import DocsTab from "@/components/DocsTab";
import SolverStatus from "@/components/SolverStatus";
import WorkflowImport from "@/components/WorkflowImport";
import CostModelsPanel from "@/components/CostModelsPanel";
import CollaborationPanel from "@/components/CollaborationPanel";
import QPUDashboard from "@/components/QPUDashboard";
import WorkflowComparison from "@/components/WorkflowComparison";
import AgentHealthCards from "@/components/AgentHealthCards";
import ActivityTimeline from "@/components/ActivityTimeline";
import SavingsReport from "@/components/SavingsReport";
import FirstOptimizationCard from "@/components/dashboard/FirstOptimizationCard";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import DashboardDialogs from "@/components/dashboard/DashboardDialogs";
import Leaderboard from "@/components/Leaderboard";
import Announcements from "@/components/Announcements";
import CodeExamplesTab from "@/components/CodeExamplesTab";
import ParallelDAG from "@/components/ParallelDAG";
import ModelProvisioningHub from "@/components/ModelProvisioningHub";
import GatewayPlayground from "@/components/GatewayPlayground";
import UsageCharts from "@/components/UsageCharts";
import KnowledgeBase from "@/components/KnowledgeBase";
import KnowledgeExplorer from "@/components/kb/KnowledgeExplorer";
import CostComparisonVisualizer from "@/components/CostComparisonVisualizer";
import ProjectsHub from "@/components/projects/ProjectsHub";
import { RAGSavingsCard } from "@/components/RAGDashboardCards";
import ProviderCatalog from "@/components/ProviderCatalog";
import ProviderStatusDashboard from "@/components/ProviderStatusDashboard";
import APIKeysTab from "@/components/APIKeysTab";
import ProfileTab from "@/components/ProfileTab";
import TeamManagementTab from "@/components/TeamManagementTab";
import UsageDashboardTab from "@/components/UsageDashboardTab";
import BillingTab from "@/components/BillingTab";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import ComparisonWidget from "@/components/ComparisonWidget";
import DWaveOptimizationWidget from "@/components/DWaveOptimizationWidget";
import OverviewDashboard from "@/components/OverviewDashboard";
import AnalyticsDeepDive from "@/components/AnalyticsDeepDive";
import ProviderManagement from "@/components/ProviderManagement";
import ModelCatalog from "@/components/ModelCatalog";
import OptimizationEngine from "@/components/OptimizationEngine";
import SettingsPage from "@/components/SettingsPage";
import SwarmBuilder from "@/components/SwarmBuilder";
import ABTestingSection from "@/components/ABTestingSection";
import BlockchainLedger from "@/components/BlockchainLedger";
import HavenDeveloperPortal from "@/components/HavenDeveloperPortal";
import HarvestDeveloperPortal from "@/components/HarvestDeveloperPortal";
import CrossVerticalDashboard from "@/components/CrossVerticalDashboard";
import TenantAdminPanel from "@/components/TenantAdminPanel";
import SalesSwarmPortal from "@/components/SalesSwarmPortal";
import { LABELS } from "@/lib/language";
import {
  staggerContainer, staggerItem, hoverScale, nodeAppear,
} from "@/config/animations";
import ProxyAnalytics from "@/components/ProxyAnalytics";
import {
  AGENT_COLORS, formatSolverBadge, formatProviderDistribution, hashCode,
} from "@/config/dashboardConstants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ initialTab }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || initialTab || "optimize";
  });

  // Expose setActiveTab globally for copilot navigation
  useEffect(() => {
    window.__qnnealSetActivePage = (tab) => setActiveTab(tab);
    window.__qnnealRouterNavigate = (path) => navigate(path);
    return () => { delete window.__qnnealSetActivePage; delete window.__qnnealRouterNavigate; };
  }, [navigate]);

  // Handle browser back/forward + popstate to sync tab state
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [optimizationResult, setOptimizationResult] = useState(() => {
    try { const saved = localStorage.getItem('qn_last_optimization'); return saved ? JSON.parse(saved) : null; } catch { return null; }
  });
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizations, setOptimizations] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);

  // Team state
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamWorkflows, setTeamWorkflows] = useState([]);
  const [teamActivity, setTeamActivity] = useState([]);
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  // Comparison state
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Scheduled optimizations state
  const [schedules, setSchedules] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [newScheduleWorkflow, setNewScheduleWorkflow] = useState("");
  const [newScheduleCron, setNewScheduleCron] = useState("0 */6 * * *");
  const [newScheduleSuper, setNewScheduleSuper] = useState(false);

  // Real-time presence state
  const [teamPresence, setTeamPresence] = useState([]);

  // Email invite state
  const [emailInviteDialogOpen, setEmailInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  // Share state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  // Analytics state
  const [usageAnalytics, setUsageAnalytics] = useState(null);

  // Solver status state
  const [solverStatus, setSolverStatus] = useState(null);

  // Adaptive config state (routing profile)
  const [activeProfile, setActiveProfile] = useState(null);
  const [activeProviders, setActiveProviders] = useState([]);

  // New Money View state
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("qn_viewMode") || "standard");
  const [managedKeyStatus, setManagedKeyStatus] = useState(null);
  const [optimizeStep, setOptimizeStep] = useState(null);
  const [celebrationAmount, setCelebrationAmount] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSubscription();
    fetchOptimizations();
    fetchWorkflows();
    fetchTeams();
    fetchSchedules();
    fetchUsageAnalytics();
    fetchAdaptiveConfig();
    fetchSolverStatus();
    fetchManagedKeyStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial fetch; fetchTeams/fetchWorkflows intentionally excluded
  }, []);

  const fetchManagedKeyStatus = async () => {
    try {
      const res = await axios.get(`${API}/managed-keys/status`);
      setManagedKeyStatus(res.data);
    } catch {}
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data);
      if (response.data.length > 0 && !workflow) {
        setWorkflow(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch workflows:", error);
    }
  };

  const fetchAdaptiveConfig = async () => {
    try {
      const res = await axios.get(`${API}/user/adaptive-config`);
      const config = res.data.adaptive_config;
      if (config?.routingProfileId) {
        setActiveProfile(config.routingProfileId);
        setActiveProviders(config.providers || []);
      }
    } catch {
      // Non-blocking
    }
  };

  const fetchSolverStatus = async () => {
    try {
      const res = await axios.get(`${API}/solver/status`);
      setSolverStatus(res.data);
    } catch {
      // Non-blocking
    }
  };

  // Team functions
  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
      if (response.data.length > 0 && !selectedTeam) {
        loadTeam(response.data[0].team_id);
      }
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    }
  };

  const loadTeam = async (teamId) => {
    try {
      const [teamRes, wfRes, actRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}`),
        axios.get(`${API}/teams/${teamId}/workflows`),
        axios.get(`${API}/teams/${teamId}/activity`)
      ]);
      setSelectedTeam(teamRes.data);
      setTeamMembers(teamRes.data.members || []);
      setTeamWorkflows(wfRes.data);
      setTeamActivity(actRes.data);
    } catch (error) {
      console.error("Failed to load team:", error);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const response = await axios.post(`${API}/teams`, { name: newTeamName });
      toast.success(`Team "${newTeamName}" created!`);
      setCreateTeamDialogOpen(false);
      setNewTeamName("");
      fetchTeams();
      loadTeam(response.data.team_id);
    } catch (error) {
      toast.error("Failed to create team");
    }
  };

  const handleCreateInvite = async () => {
    if (!selectedTeam) return;
    try {
      const response = await axios.post(`${API}/teams/${selectedTeam.team_id}/invite`, { role: "member" });
      const fullUrl = `${window.location.origin}/teams/join/${response.data.invite_code}`;
      setInviteLink(fullUrl);
      setInviteDialogOpen(true);
    } catch (error) {
      toast.error("Failed to create invite");
    }
  };

  const handleJoinTeam = async () => {
    if (!joinCode.trim()) return;
    try {
      const code = joinCode.includes("/") ? joinCode.split("/").pop() : joinCode;
      const response = await axios.post(`${API}/teams/join/${code}`);
      toast.success(response.data.message);
      setJoinDialogOpen(false);
      setJoinCode("");
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join team");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedTeam) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}/members/${memberId}`);
      toast.success("Member removed");
      loadTeam(selectedTeam.team_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Comparison functions
  const toggleCompareSelection = (optId) => {
    setCompareSelected(prev => 
      prev.includes(optId) 
        ? prev.filter(id => id !== optId)
        : prev.length < 5 ? [...prev, optId] : prev
    );
  };

  const handleCompare = async () => {
    if (compareSelected.length < 2) {
      toast.error("Select at least 2 optimizations");
      return;
    }
    setComparing(true);
    try {
      const response = await axios.post(`${API}/optimizations/compare`, { optimization_ids: compareSelected });
      setCompareResult(response.data);
    } catch (error) {
      toast.error("Failed to compare optimizations");
    } finally {
      setComparing(false);
    }
  };

  // Schedule functions
  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
    }
  };

  const handleCreateSchedule = async () => {
    if (!newScheduleWorkflow) {
      toast.error("Select a workflow");
      return;
    }
    try {
      await axios.post(`${API}/schedules`, {
        workflow_id: newScheduleWorkflow,
        cron_expression: newScheduleCron,
        super_optimize: newScheduleSuper
      });
      toast.success("Schedule created!");
      setScheduleDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create schedule");
    }
  };

  const handleToggleSchedule = async (scheduleId) => {
    try {
      const response = await axios.put(`${API}/schedules/${scheduleId}/toggle`);
      toast.success(response.data.enabled ? "Schedule enabled" : "Schedule paused");
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to toggle schedule");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`);
      toast.success("Schedule deleted");
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  // Email invite
  const handleEmailInvite = async () => {
    if (!inviteEmail || !selectedTeam) return;
    try {
      const response = await axios.post(`${API}/teams/${selectedTeam.team_id}/invite/email`, { email: inviteEmail });
      if (response.data.email_sent) {
        toast.success(`Invite sent to ${inviteEmail}`);
      } else {
        toast.success("Invite created! (Email delivery requires Resend API key)");
        setInviteLink(response.data.invite_url);
        setInviteDialogOpen(true);
      }
      setEmailInviteDialogOpen(false);
      setInviteEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    }
  };

  // Share workflow
  const handleShareWorkflow = async (workflowId) => {
    try {
      const response = await axios.post(`${API}/workflows/${workflowId}/share`);
      const fullUrl = response.data.share_url || `${window.location.origin}/shared/${response.data.share_code}`;
      setShareUrl(fullUrl);
      setShareDialogOpen(true);
    } catch (error) {
      toast.error("Failed to create share link");
    }
  };

  // Usage analytics
  const fetchUsageAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/usage`);
      setUsageAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  };

  // Cursor presence via WebSocket
  useEffect(() => {
    if (!selectedTeam) return;
    const ws = window.__qnneal_ws;
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "presence_update" && data.team_id === selectedTeam.team_id) {
          setTeamPresence(data.users || []);
        }
      } catch {}
    };

    ws.addEventListener("message", handleMessage);

    // Send our presence
    const sendPresence = () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: "cursor_update",
          team_id: selectedTeam.team_id,
          name: user?.name || "User",
          page: "dashboard",
          color: `hsl(${Math.abs(hashCode(user?.user_id || "")) % 360}, 70%, 60%)`
        }));
      }
    };

    sendPresence();
    const interval = setInterval(sendPresence, 10000);

    return () => {
      ws.removeEventListener("message", handleMessage);
      clearInterval(interval);
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "leave_presence", team_id: selectedTeam.team_id }));
      }
    };
  }, [selectedTeam, user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/optimizations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const fetchOptimizations = async () => {
    try {
      const response = await axios.get(`${API}/optimizations`);
      setOptimizations(response.data);
    } catch (error) {
      console.error("Failed to fetch optimizations:", error);
    }
  };

  const handleCreateDemoWorkflow = async () => {
    try {
      const response = await axios.post(`${API}/demo/workflow`);
      setWorkflow(response.data);
      setOptimizationResult(null);
      fetchWorkflows();
      toast.success("Demo workflow created!");
    } catch (error) {
      toast.error("Failed to create workflow");
    }
  };

  const handleCreateParallelDemo = async () => {
    try {
      const response = await axios.post(`${API}/demo/parallel-workflow`);
      setWorkflow(response.data);
      setOptimizationResult(null);
      fetchWorkflows();
      toast.success("Parallel showcase pipeline created — hit Optimize to see the magic!");
    } catch (error) {
      toast.error("Failed to create parallel workflow");
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    try {
      await axios.delete(`${API}/workflows/${workflowToDelete.workflow_id}`);
      toast.success("Workflow deleted");
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      if (workflow?.workflow_id === workflowToDelete.workflow_id) {
        setWorkflow(null);
      }
      fetchWorkflows();
    } catch (error) {
      toast.error("Failed to delete workflow");
    }
  };

  const handleCreateVersion = async (workflowId) => {
    try {
      const response = await axios.post(`${API}/workflows/${workflowId}/version`);
      toast.success(`Version ${response.data.version_number} saved`);
    } catch (error) {
      toast.error("Failed to create version");
    }
  };

  const handleViewVersions = async (workflowId) => {
    try {
      const response = await axios.get(`${API}/workflows/${workflowId}/versions`);
      setVersions(response.data);
      setVersionsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load versions");
    }
  };

  const handleRestoreVersion = async (workflowId, versionId) => {
    try {
      await axios.post(`${API}/workflows/${workflowId}/restore/${versionId}`);
      toast.success("Workflow restored");
      setVersionsDialogOpen(false);
      fetchWorkflows();
    } catch (error) {
      toast.error("Failed to restore version");
    }
  };

  const handleOptimize = async (superOptimize = false) => {
    if (!workflow) {
      toast.error("No workflow to optimize. Create one first.");
      return;
    }

    setIsOptimizing(true);
    setOptimizationResult(null);
    setCelebrationAmount(null);
    setOptimizeStep(LABELS.opt_analyzing);

    const steps = [LABELS.opt_analyzing, LABELS.opt_finding, LABELS.opt_almost];
    let stepIdx = 0;
    const stepTimer = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) setOptimizeStep(steps[stepIdx]);
    }, 2000);

    const minDisplayTime = new Promise(resolve => setTimeout(resolve, superOptimize ? 4000 : 2500));

    try {
      const [response] = await Promise.all([
        axios.post(`${API}/optimize`, {
          workflow_id: workflow.workflow_id,
          super_optimize: superOptimize
        }),
        minDisplayTime
      ]);

      clearInterval(stepTimer);
      setOptimizationResult(response.data);
      try { localStorage.setItem('qn_last_optimization', JSON.stringify(response.data)); } catch {}
      fetchStats();
      fetchOptimizations();

      const saved = response.data.original_cost - response.data.optimized_cost;
      setCelebrationAmount(saved);
      setOptimizeStep(null);

      // Celebration animation
      confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
      toast.success(`Done! You just saved $${saved.toFixed(2)}`);
    } catch (error) {
      clearInterval(stepTimer);
      setOptimizeStep(null);
      const message = error.response?.data?.detail || "Something went wrong. We're on it.";
      toast.error(message);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const statCards = [
    { icon: Activity, label: "Total Optimizations", value: stats?.total_optimizations || 0, color: "var(--qn-primary)" },
    { icon: DollarSign, label: "Avg. Cost Reduction", value: stats?.avg_cost_reduction || 0, suffix: "%", color: "var(--qn-success)" },
    { icon: Zap, label: "Avg. Time Reduction", value: stats?.avg_time_reduction || 0, suffix: "%", color: "var(--qn-secondary)" },
    { icon: Sparkles, label: "Total Saved", value: stats?.total_cost_saved || 0, prefix: "$", decimals: 2, color: "#F59E0B" },
  ];

  const [devExpanded, setDevExpanded] = useState(() => localStorage.getItem("qn_dev_expanded") === "true");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("qn_sidebar_collapsed") === "true");
  // embed=1: rendered inside the new dashboard (AppShell) — hide the classic sidebar.
  const embedded = new URLSearchParams(window.location.search).get("embed") === "1";
  const [workflowSubTab, setWorkflowSubTab] = useState("my-workflows");
  const [modelSubTab, setModelSubTab] = useState("available");

  // Auto-switch to playground sub-tab when navigating to models tab
  React.useEffect(() => {
    if (activeTab === "models") setModelSubTab("playground");
  }, [activeTab]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("qn_viewMode", mode);
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)] grid-lines flex">
        {/* Sidebar */}
        {!embedded && <DashboardSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          devExpanded={devExpanded}
          setDevExpanded={(fn) => {
            setDevExpanded((p) => { const next = typeof fn === "function" ? fn(p) : fn; localStorage.setItem("qn_dev_expanded", next); return next; });
          }}
          collapsed={sidebarCollapsed}
          setCollapsed={(v) => { setSidebarCollapsed(v); localStorage.setItem("qn_sidebar_collapsed", v); }}
          user={user}
          solverStatus={solverStatus}
          activeProfile={activeProfile}
          activeProviders={activeProviders}
          onLogout={handleLogout}
        />}

        {/* Main Content */}
        <main className={`${embedded ? "ml-0" : sidebarCollapsed ? "ml-[72px]" : "ml-[240px]"} flex-1 min-h-screen px-8 py-8 max-w-[1400px] transition-all duration-300`}>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === "optimize" && (
              <motion.div
                key="optimize"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Onboarding Checklist */}
                <OnboardingChecklist />

                {/* D-Wave Quantum Engine Status — visible to all users */}
                <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)] p-5" data-testid="dwave-dashboard-widget">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--qn-foreground)]">D-Wave Quantum Engine</h3>
                      <p className="text-xs text-[var(--qn-foreground-subtle)]">Advantage2 QPU · Pegasus Topology</p>
                    </div>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid="qpu-badge">
                      QPU Available
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg bg-[var(--qn-background-tertiary)] border border-[var(--qn-border)] p-3 text-center">
                      <div className="text-lg font-bold text-cyan-400">1,000/s</div>
                      <div className="text-xs text-[var(--qn-foreground-subtle)]">Annealing Cycles</div>
                    </div>
                    <div className="rounded-lg bg-[var(--qn-background-tertiary)] border border-[var(--qn-border)] p-3 text-center">
                      <div className="text-lg font-bold text-emerald-400">Active</div>
                      <div className="text-xs text-[var(--qn-foreground-subtle)]">Classical Engine</div>
                    </div>
                    <div className="rounded-lg bg-[var(--qn-background-tertiary)] border border-[var(--qn-border)] p-3 text-center">
                      <div className="text-lg font-bold text-purple-400">5,760</div>
                      <div className="text-xs text-[var(--qn-foreground-subtle)]">Qubits</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-[var(--qn-foreground-subtle)]">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>
                    <span>Morency QSI Framework v2.1 · Last calibration: 2 hours ago</span>
                  </div>
                </div>

                {/* First Optimization Hero Card */}
                <FirstOptimizationCard onOptimizationComplete={(data) => {
                  setOptimizationResult(data);
                  try { localStorage.setItem('qn_last_optimization', JSON.stringify(data)); } catch {}
                  fetchStats();
                  fetchOptimizations();
                }} />
                {/* Simplified Optimize Section (Money/Manager view) */}
                {viewMode !== "engineer" && (
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* One-Click Optimize Card */}
                    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      <CardContent className="p-8 flex flex-col items-center justify-center min-h-[300px]">
                        {isOptimizing ? (
                          <div className="text-center space-y-4">
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                              className="w-16 h-16 rounded-full border-4 border-[var(--qn-primary)]/20 border-t-[var(--qn-primary)] mx-auto"
                            />
                            <p className="text-lg text-[var(--qn-foreground-muted)]" data-testid="optimize-step-label">{optimizeStep}</p>
                          </div>
                        ) : celebrationAmount !== null && !isOptimizing ? (
                          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center space-y-3">
                            <Sparkles className="w-12 h-12 text-[var(--qn-success)] mx-auto" />
                            <p className="text-xl font-semibold text-[var(--qn-success)]" data-testid="celebration-message">
                              Done! You just saved ${celebrationAmount.toFixed(2)}
                            </p>
                            <Button variant="outline" size="sm" onClick={() => setCelebrationAmount(null)} data-testid="optimize-again-btn" data-automation-id="optimize-again-btn">
                              Optimize Again
                            </Button>
                          </motion.div>
                        ) : (
                          <div className="text-center space-y-4">
                            {!workflow && (
                              <div className="flex items-center justify-center gap-3 mb-4">
                                <Button onClick={handleCreateDemoWorkflow} variant="outline" data-testid="create-demo-workflow-btn" data-automation-id="create-demo-workflow-btn">
                                  <Plus className="w-4 h-4 mr-2" />Create Demo Workflow
                                </Button>
                                <Button onClick={handleCreateParallelDemo} variant="outline" className="border-[var(--qn-primary)]/40 text-[var(--qn-primary)]" data-testid="create-parallel-demo-btn" data-automation-id="create-parallel-demo-btn">
                                  <GitCompare className="w-4 h-4 mr-2" />Parallel Demo
                                </Button>
                              </div>
                            )}
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                              <Button
                                onClick={() => {
                                  if (managedKeyStatus && !managedKeyStatus.has_own_keys && managedKeyStatus.remaining <= 0) {
                                    navigate("/settings");
                                    return;
                                  }
                                  handleOptimize(false);
                                }}
                                disabled={!workflow}
                                className="optimize-btn btn-primary py-8 px-12 text-xl pulse-glow rounded-2xl"
                                data-testid="optimize-btn"
                                data-automation-id="run-optimization-btn"
                              >
                                <Zap className="w-6 h-6 mr-3" />
                                {managedKeyStatus && !managedKeyStatus.has_own_keys && managedKeyStatus.remaining <= 0
                                  ? "Connect API Keys to Continue"
                                  : managedKeyStatus && !managedKeyStatus.has_own_keys && managedKeyStatus.has_optimized
                                    ? `Run Optimization (${managedKeyStatus.remaining} free left)`
                                    : t('dashboard.optimizeButton', LABELS.optimize_button)}
                              </Button>
                            </motion.div>
                            <button
                              onClick={() => handleOptimize(true)}
                              disabled={!workflow}
                              className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)] transition-colors"
                              data-testid="super-optimize-btn"
                              data-automation-id="super-optimize-btn"
                            >
                              or try <span className="underline">{t('dashboard.deepOptimize', 'Deep Optimization (takes longer, saves more)')}</span>
                            </button>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Optimization Results (when available) */}
                    <AnimatePresence>
                      {optimizationResult ? (
                        <motion.div
                          initial={{ opacity: 0, y: 20, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                        >
                          <Card className="bg-[var(--qn-card)] border-[var(--qn-primary)]/30">
                            <CardHeader>
                              <CardTitle className="font-outfit text-[var(--qn-primary)] flex items-center gap-2">
                                <Sparkles className="w-5 h-5" />
                                Results
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Cost Saved</div>
                                  <div className="metric-value text-2xl text-[var(--qn-success)]">
                                    <AnimatedCounter value={optimizationResult.cost_reduction_percent} suffix="%" />
                                  </div>
                                  <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
                                    ${optimizationResult.original_cost.toFixed(4)} → ${optimizationResult.optimized_cost.toFixed(4)}
                                  </div>
                                </div>
                                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Speed Boost</div>
                                  <div className="metric-value text-2xl text-[var(--qn-primary)]">
                                    <AnimatedCounter value={optimizationResult.time_reduction_percent} suffix="%" />
                                  </div>
                                  <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
                                    {optimizationResult.original_time.toFixed(2)}s → {optimizationResult.optimized_time.toFixed(2)}s
                                  </div>
                                </div>
                              </div>
                              {/* Money View: Optimization Breakdown */}
                              {viewMode === "money" && optimizationResult.dag_analysis && (
                                <div className="bg-[var(--qn-background)] rounded-lg p-4 mb-4" data-testid="money-optimization-breakdown">
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-3">Optimization Breakdown</div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <div className="text-xs text-[var(--qn-foreground-subtle)]">Model routing saved</div>
                                      <div className="text-lg font-mono font-bold text-[var(--qn-success)]">
                                        ${(optimizationResult.original_cost - optimizationResult.optimized_cost).toFixed(4)} ({optimizationResult.cost_reduction_percent}%)
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-[var(--qn-foreground-subtle)]">Parallelization saved</div>
                                      <div className="text-lg font-mono font-bold text-[var(--qn-primary)]">
                                        {(optimizationResult.dag_analysis.time_saved_by_parallelization_ms / 1000).toFixed(1)}s ({optimizationResult.dag_analysis.time_saved_by_parallelization_pct}% faster)
                                      </div>
                                    </div>
                                  </div>
                                  {/* Time savings bar */}
                                  <div className="mt-3">
                                    <div className="flex justify-between text-xs text-[var(--qn-foreground-subtle)] mb-1">
                                      <span>Sequential: {(optimizationResult.dag_analysis.original_sequential_time_ms / 1000).toFixed(1)}s</span>
                                      <span>Parallel: {(optimizationResult.dag_analysis.parallelized_time_ms / 1000).toFixed(1)}s</span>
                                    </div>
                                    <div className="w-full h-2 bg-[var(--qn-border)] rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-[var(--qn-primary)] rounded-full transition-all duration-1000"
                                        style={{ width: `${Math.max(5, 100 - (optimizationResult.dag_analysis.time_saved_by_parallelization_pct || 0))}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              {viewMode !== "money" && (
                                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                  {/* Parallelization Summary (Manager view) */}
                                  {viewMode === "manager" && optimizationResult.dag_analysis && (
                                    <div className="mb-4 p-3 rounded-lg bg-[var(--qn-primary-muted)] border border-[var(--qn-primary)]/10" data-testid="parallelization-summary">
                                      <div className="text-xs font-semibold text-[var(--qn-primary)] mb-2">Parallelization Summary</div>
                                      <div className="grid grid-cols-3 gap-3 text-xs">
                                        <div>
                                          <span className="text-[var(--qn-foreground-subtle)]">Original:</span>
                                          <div className="font-mono text-[var(--qn-foreground-muted)]">Sequential ({(optimizationResult.dag_analysis.original_sequential_time_ms / 1000).toFixed(1)}s)</div>
                                        </div>
                                        <div>
                                          <span className="text-[var(--qn-foreground-subtle)]">Optimized:</span>
                                          <div className="font-mono text-[var(--qn-foreground-muted)]">{(optimizationResult.dag_analysis.parallel_groups || []).length} groups, {optimizationResult.dag_analysis.max_concurrent_tasks} concurrent ({(optimizationResult.dag_analysis.parallelized_time_ms / 1000).toFixed(1)}s)</div>
                                        </div>
                                        <div>
                                          <span className="text-[var(--qn-foreground-subtle)]">Time Saved:</span>
                                          <div className="font-mono text-[var(--qn-success)]">{optimizationResult.dag_analysis.time_saved_by_parallelization_pct}%</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-3">Agent Re-assignments</div>
                                  <div className="space-y-2">
                                    {optimizationResult.optimized_tasks?.map((task, i) => {
                                      const tierStyle = { standard: "text-gray-500 dark:text-gray-400", high: "text-blue-400", critical: "text-orange-400" };
                                      const dagGroups = optimizationResult.dag_analysis?.parallel_groups || [];
                                      const groupIdx = dagGroups.findIndex(g => g.tasks.includes(task.id));
                                      const group = dagGroups[groupIdx];
                                      return (
                                        <div key={i} className="flex items-center justify-between text-sm">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[var(--qn-foreground-muted)]">{task.name}</span>
                                            {task.complexity_tier && (
                                              <span className={`text-[10px] font-bold ${tierStyle[task.complexity_tier] || "text-gray-500 dark:text-gray-400"}`}>
                                                {task.complexity_tier === "critical" ? "C" : task.complexity_tier === "high" ? "H" : "S"}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {viewMode === "manager" && group && (
                                              <span className="text-[10px] text-[var(--qn-foreground-subtle)]">
                                                G{groupIdx + 1}{group.can_parallelize ? " (parallel)" : ""}
                                              </span>
                                            )}
                                            <Badge variant="outline" className="text-xs border-[var(--qn-border)]">{task.original_agent}</Badge>
                                            <span className="text-[var(--qn-foreground-subtle)]">&rarr;</span>
                                            <Badge className="text-xs bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]">{task.optimized_agent}</Badge>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {viewMode === "engineer" && (
                                <div className="mt-4 space-y-1">
                                  <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono" data-testid="solver-info-line">
                                    Agentic optimization via {formatSolverBadge(optimizationResult.solver_type)} in {optimizationResult.optimization_time_ms}ms
                                    {optimizationResult.qpu_access_time_us > 0 && ` (QPU: ${(optimizationResult.qpu_access_time_us / 1000).toFixed(1)}ms)`}
                                  </div>
                                  {formatProviderDistribution(optimizationResult) && (
                                    <div className="text-xs text-[var(--qn-foreground-subtle)]" data-testid="provider-distribution">
                                      {formatProviderDistribution(optimizationResult)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ) : !isOptimizing && !celebrationAmount && (
                        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                          <CardContent className="p-8 flex flex-col items-center justify-center min-h-[300px] text-center">
                            <Zap className="w-12 h-12 text-[var(--qn-foreground-subtle)] mb-4 opacity-30" />
                            <p className="text-[var(--qn-foreground-muted)]">Run an optimization to see your savings</p>
                          </CardContent>
                        </Card>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Engineer View: Full technical panels */}
                {viewMode === "engineer" && (
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Workflow Panel */}
                    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      <CardHeader>
                        <CardTitle className="font-outfit flex items-center gap-2">
                          <Workflow className="w-5 h-5 text-[var(--qn-primary)]" />
                          Workflow DAG
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {!workflow ? (
                          <div className="text-center py-12">
                            <Workflow className="w-16 h-16 mx-auto text-[var(--qn-foreground-subtle)] mb-4 opacity-50" />
                            <p className="text-[var(--qn-foreground-muted)] mb-6">No workflow loaded</p>
                            <div className="flex items-center justify-center gap-3">
                              <Button onClick={handleCreateDemoWorkflow} className="btn-primary" data-testid="create-demo-workflow-btn-eng" data-automation-id="create-demo-workflow-btn-eng">
                                Create Demo Workflow
                              </Button>
                              <Button onClick={handleCreateParallelDemo} variant="outline" className="border-[var(--qn-primary)]/40 text-[var(--qn-primary)] hover:bg-[var(--qn-primary-muted)]" data-testid="create-parallel-demo-btn-eng" data-automation-id="create-parallel-demo-btn-eng">
                                <GitCompare className="w-4 h-4 mr-2" />Parallel Demo
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <ParallelDAG
                            dagAnalysis={optimizationResult?.dag_analysis}
                            optimizedTasks={optimizationResult?.optimized_tasks}
                            tasks={workflow.tasks}
                            optimized={!!optimizationResult}
                            solverInfo={optimizationResult ? {
                              solver_type: optimizationResult.solver_type,
                              time: optimizationResult.optimization_time_ms,
                              qpu: optimizationResult.qpu_access_time_us || 0,
                            } : null}
                          />
                        )}
                      </CardContent>
                    </Card>

                    {/* Technical Optimization Panel */}
                    <div className="space-y-6">
                      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                        <CardHeader>
                          <CardTitle className="font-outfit flex items-center gap-2">
                            <Zap className="w-5 h-5 text-[var(--qn-primary)]" />
                            Optimization Controls
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <Button
                              onClick={() => handleOptimize(false)}
                              disabled={!workflow || isOptimizing}
                              className="w-full optimize-btn btn-primary py-6 text-lg pulse-glow"
                              data-testid="optimize-btn-eng"
                              data-automation-id="optimize-btn-eng"
                            >
                              <Play className="w-5 h-5 mr-2" />
                              Optimize
                            </Button>
                            <Button
                              onClick={() => handleOptimize(true)}
                              disabled={!workflow || isOptimizing}
                              variant="outline"
                              className="w-full py-6 text-lg border-[var(--qn-secondary)]/30 text-[var(--qn-secondary)]"
                              data-testid="super-optimize-btn-eng"
                              title={solverStatus?.quantum_status !== "connected" ? "Quantum solver unavailable — using deep classical optimization" : "Uses quantum-enhanced solver for maximum optimization"}
                            >
                              <Sparkles className="w-5 h-5 mr-2" />
                              Super Optimize
                            </Button>
                          </div>
                          {isOptimizing && optimizeStep && (
                            <div className="text-center text-sm text-[var(--qn-foreground-muted)]">{optimizeStep}</div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Full Results */}
                      <AnimatePresence>
                        {optimizationResult && (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            <Card className="bg-[var(--qn-card)] border-[var(--qn-primary)]/30">
                              <CardHeader>
                                <CardTitle className="font-outfit text-[var(--qn-primary)] flex items-center gap-2">
                                  <Sparkles className="w-5 h-5" />
                                  Optimization Results
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                    <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Token Cost Reduction</div>
                                    <div className="metric-value text-2xl text-[var(--qn-success)]">
                                      <AnimatedCounter value={optimizationResult.cost_reduction_percent} suffix="%" />
                                    </div>
                                    <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
                                      ${optimizationResult.original_cost.toFixed(4)} → ${optimizationResult.optimized_cost.toFixed(4)}
                                    </div>
                                  </div>
                                  <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                    <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Time Reduction</div>
                                    <div className="metric-value text-2xl text-[var(--qn-secondary)]">
                                      <AnimatedCounter value={optimizationResult.time_reduction_percent} suffix="%" />
                                    </div>
                                    <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
                                      {optimizationResult.original_time.toFixed(2)}s → {optimizationResult.optimized_time.toFixed(2)}s
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-[var(--qn-background)] rounded-lg p-4">
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-3">Agent Re-assignments</div>
                                  <div className="space-y-2">
                                    {optimizationResult.optimized_tasks?.map((task, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-[var(--qn-foreground-muted)]">{task.name}</span>
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs border-[var(--qn-border)]">{task.original_agent}</Badge>
                                          <span className="text-[var(--qn-foreground-subtle)]">→</span>
                                          <Badge className="text-xs bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]">{task.optimized_agent}</Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="mt-4 space-y-1">
                                  <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono" data-testid="solver-info-eng">
                                    Agentic optimization via {formatSolverBadge(optimizationResult.solver_type)} in {optimizationResult.optimization_time_ms}ms
                                    {optimizationResult.qpu_access_time_us > 0 && ` (QPU: ${(optimizationResult.qpu_access_time_us / 1000).toFixed(1)}ms)`}
                                  </div>
                                  {formatProviderDistribution(optimizationResult) && (
                                    <div className="text-xs text-[var(--qn-foreground-subtle)]" data-testid="provider-distribution-eng">
                                      {formatProviderDistribution(optimizationResult)}
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Agent Health Cards */}
                <AgentHealthCards viewMode={viewMode} />

                {/* Activity Timeline */}
                <ActivityTimeline />

                {/* Leaderboard + Announcements side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Leaderboard />
                  <Announcements />
                </div>
              </motion.div>
            )}

            {activeTab === "optimize" && viewMode === "engineer" && (
              <div className="mt-6"><SolverStatus /></div>
            )}

            {activeTab === "optimize" && viewMode === "engineer" && (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DWaveOptimizationWidget />
                <CostModelsPanel />
              </div>
            )}

            {activeTab === "overview" && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <OverviewDashboard />
              </motion.div>
            )}

            {activeTab === "analytics" && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {/* Proxy Analytics */}
                <ProxyAnalytics />
                <div className="mt-6">
                  <SavingsReport />
                </div>
                <div className="mt-8">
                  <CostComparisonVisualizer />
                </div>
                {viewMode === "engineer" && (
                  <div className="mt-8 space-y-8">
                    <QPUDashboard />
                    <WorkflowComparison />
                  </div>
                )}
                <div className="mt-8">
                  <AnalyticsDeepDive />
                </div>
                <div className="mt-8">
                  <ABTestingSection />
                </div>
                <div className="mt-8">
                  <BlockchainLedger />
                </div>
              </motion.div>
            )}

            {activeTab === "model-catalog" && (
              <motion.div
                key="model-catalog"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ModelCatalog />
              </motion.div>
            )}

            {activeTab === "models" && (
              <motion.div
                key="models"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ComparisonWidget compact />
                <div className="mt-6">
                {/* Sub-tab switcher */}
                <div className="flex gap-1 mb-6 p-1 bg-[var(--qn-surface)] rounded-lg w-fit" data-testid="models-sub-tabs">
                  <button
                    onClick={() => setModelSubTab("available")}
                    data-testid="models-tab-available"
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      modelSubTab === "available"
                        ? "bg-[var(--qn-card)] text-[var(--qn-foreground)] shadow-sm"
                        : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                    }`}
                  >
                    Available Models
                  </button>
                  <button
                    onClick={() => setModelSubTab("playground")}
                    data-testid="models-tab-playground"
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      modelSubTab === "playground"
                        ? "bg-[var(--qn-card)] text-[var(--qn-foreground)] shadow-sm"
                        : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                    }`}
                  >
                    Playground
                  </button>
                </div>
                {modelSubTab === "available" ? (
                  <>
                    <ModelProvisioningHub />
                    <div className="mt-6"><UsageCharts /></div>
                  </>
                ) : (
                  <GatewayPlayground />
                )}
                </div>
              </motion.div>
            )}

            {activeTab === "provider-mgmt" && (
              <motion.div
                key="provider-mgmt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ProviderManagement />
              </motion.div>
            )}

            {activeTab === "providers" && (
              <motion.div
                key="providers"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ComparisonWidget />
                <div className="mt-8">
                  <ProviderCatalog />
                </div>
              </motion.div>
            )}

            {activeTab === "knowledge" && (
              <motion.div
                key="knowledge"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <RAGSavingsCard />
                <div className="mt-6"><KnowledgeBase /></div>
              </motion.div>
            )}

            {activeTab === "kb" && (
              <motion.div
                key="kb"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <KnowledgeExplorer />
              </motion.div>
            )}

            {activeTab === "projects" && (
              <motion.div
                key="projects"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ProjectsHub />
              </motion.div>
            )}

            {activeTab === "status" && (
              <motion.div
                key="status"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ProviderStatusDashboard />
              </motion.div>
            )}

            {activeTab === "api-keys" && (
              <motion.div
                key="api-keys"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <APIKeysTab />
              </motion.div>
            )}

            {activeTab === "optimization-engine" && (
              <motion.div
                key="optimization-engine"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <OptimizationEngine />
              </motion.div>
            )}

            {activeTab === "swarm" && (
              <motion.div
                key="swarm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SwarmBuilder />
              </motion.div>
            )}

            {activeTab === "haven-portal" && (
              <motion.div
                key="haven-portal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <HavenDeveloperPortal />
              </motion.div>
            )}

            {activeTab === "harvest-portal" && (
              <motion.div
                key="harvest-portal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <HarvestDeveloperPortal />
              </motion.div>
            )}

            {activeTab === "cross-vertical" && (
              <motion.div
                key="cross-vertical"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <CrossVerticalDashboard />
              </motion.div>
            )}

            {activeTab === "tenant-admin" && (
              <motion.div
                key="tenant-admin"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <TenantAdminPanel />
              </motion.div>
            )}

            {activeTab === "sales-swarm" && (
              <motion.div
                key="sales-swarm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SalesSwarmPortal />
              </motion.div>
            )}

            {activeTab === "settings" && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SettingsPage />
              </motion.div>
            )}

            {activeTab === "profile" && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ProfileTab />
              </motion.div>
            )}

            {activeTab === "workflows" && (
              <motion.div
                key="workflows"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {/* Sub-tab switcher */}
                <div className="flex gap-1 mb-6 p-1 bg-[var(--qn-surface)] rounded-lg w-fit" data-testid="workflows-sub-tabs">
                  <button
                    onClick={() => setWorkflowSubTab("my-workflows")}
                    data-testid="workflows-tab-my"
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      workflowSubTab === "my-workflows"
                        ? "bg-[var(--qn-card)] text-[var(--qn-foreground)] shadow-sm"
                        : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                    }`}
                  >
                    My Workflows
                  </button>
                  <button
                    onClick={() => setWorkflowSubTab("cross-platform")}
                    data-testid="workflows-tab-cross"
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      workflowSubTab === "cross-platform"
                        ? "bg-[var(--qn-card)] text-[var(--qn-foreground)] shadow-sm"
                        : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                    }`}
                  >
                    Cross-Platform
                  </button>
                </div>
                {workflowSubTab === "cross-platform" ? (
                  <OrchestrationTab />
                ) : (
                <>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="font-outfit">Your Workflows</CardTitle>
                      <CardDescription className="text-[var(--qn-foreground-muted)]">Manage and optimize your agent workflows</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <WorkflowImport onImported={fetchWorkflows} />
                      <motion.div {...hoverScale}>
                        <Button 
                          onClick={handleCreateDemoWorkflow}
                          variant="outline"
                          className="border-[var(--qn-border)]"
                          data-testid="demo-workflow-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Demo Workflow
                        </Button>
                      </motion.div>
                      <motion.div {...hoverScale}>
                        <Button 
                          onClick={handleCreateParallelDemo}
                          variant="outline"
                          className="border-[var(--qn-primary)]/40 text-[var(--qn-primary)]"
                          data-testid="parallel-demo-workflow-btn"
                        >
                          <GitCompare className="w-4 h-4 mr-2" />
                          Parallel Demo
                        </Button>
                      </motion.div>
                      <motion.div {...hoverScale}>
                        <Button 
                          onClick={() => navigate("/workflow/new")}
                          className="btn-primary"
                          data-testid="new-workflow-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Build Workflow
                        </Button>
                      </motion.div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {workflows.length > 0 ? (
                      <motion.div 
                        variants={staggerContainer}
                        initial="initial"
                        animate="animate"
                        className="space-y-4"
                      >
                        {workflows.map((wf, idx) => (
                          <motion.div 
                            key={wf.workflow_id}
                            variants={staggerItem}
                            whileHover={{ scale: 1.01 }}
                            className={`card-dark rounded-lg p-4 cursor-pointer ${workflow?.workflow_id === wf.workflow_id ? 'border-2 border-[var(--qn-primary)]/50' : ''}`}
                            onClick={() => setWorkflow(wf)}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                  {wf.name}
                                  {wf.version > 1 && (
                                    <Badge className="text-sm bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)]">
                                      v{wf.version}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-[var(--qn-foreground-subtle)] font-mono">{wf.workflow_id}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-[var(--qn-primary)]/30">
                                  {wf.tasks.length} tasks
                                </Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setWorkflow(wf); setActiveTab("optimize"); }} className="cursor-pointer">
                                      <Play className="w-4 h-4 mr-2" />
                                      Select for Optimization
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCreateVersion(wf.workflow_id); }} className="cursor-pointer">
                                      <History className="w-4 h-4 mr-2" />
                                      Save Version
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewVersions(wf.workflow_id); }} className="cursor-pointer">
                                      <History className="w-4 h-4 mr-2" />
                                      View Versions
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-[var(--qn-border)]" />
                                    <DropdownMenuItem 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setWorkflowToDelete(wf);
                                        setDeleteDialogOpen(true);
                                      }} 
                                      className="cursor-pointer text-red-400"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {wf.tasks.slice(0, 5).map((task, i) => (
                                <Badge key={i} variant="outline" className="text-sm border-[var(--qn-border)]">
                                  {task.name}
                                </Badge>
                              ))}
                              {wf.tasks.length > 5 && (
                                <Badge variant="outline" className="text-sm text-[var(--qn-foreground-subtle)]">
                                  +{wf.tasks.length - 5} more
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                variant="ghost" size="sm"
                                className="h-7 text-sm text-[var(--qn-foreground-subtle)]"
                                onClick={(e) => { e.stopPropagation(); handleShareWorkflow(wf.workflow_id); }}
                                data-testid={`share-workflow-${wf.workflow_id}`}
                              >
                                <Share2 className="w-3 h-3 mr-1" />
                                Share
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    ) : (
                      <div className="text-center py-12 text-[var(--qn-foreground-subtle)]">
                        <Workflow className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="mb-4">No workflows yet. Create your first one!</p>
                        <motion.div {...hoverScale}>
                          <Button 
                            onClick={() => navigate("/workflow/new")}
                            className="btn-primary"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Build Custom Workflow
                          </Button>
                        </motion.div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Scheduled Optimizations */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] mt-6">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="font-outfit flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[var(--qn-secondary)]" />
                        Scheduled Optimizations
                      </CardTitle>
                      <CardDescription className="text-[var(--qn-foreground-muted)]">Automate recurring workflow optimizations</CardDescription>
                    </div>
                    <motion.div {...hoverScale}>
                      <Button onClick={() => setScheduleDialogOpen(true)} className="btn-primary" data-testid="create-schedule-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        New Schedule
                      </Button>
                    </motion.div>
                  </CardHeader>
                  <CardContent>
                    {schedules.length > 0 ? (
                      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-3">
                        {schedules.map((sched) => (
                          <motion.div
                            key={sched.schedule_id}
                            variants={staggerItem}
                            className="flex items-center justify-between card-dark rounded-lg p-4"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-3 h-3 rounded-full ${sched.enabled ? 'bg-[var(--qn-success)]' : 'bg-[var(--qn-foreground-subtle)]'}`} />
                              <div>
                                <div className="font-medium text-base">{sched.workflow_name || sched.workflow_id}</div>
                                <div className="text-sm text-[var(--qn-foreground-subtle)] font-mono flex items-center gap-2">
                                  <Timer className="w-3 h-3" />
                                  {sched.cron_expression}
                                  {sched.super_optimize && <Badge className="bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)] text-xs">Quantum</Badge>}
                                </div>
                                {sched.last_run && (
                                  <div className="text-sm text-[var(--qn-foreground-subtle)] mt-1">
                                    Last: {new Date(sched.last_run).toLocaleString()} · {sched.run_count} runs
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => handleToggleSchedule(sched.schedule_id)}
                                data-testid={`toggle-schedule-${sched.schedule_id}`}
                              >
                                <Power className={`w-4 h-4 ${sched.enabled ? 'text-[var(--qn-success)]' : 'text-[var(--qn-foreground-subtle)]'}`} />
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteSchedule(sched.schedule_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    ) : (
                      <div className="text-center py-8 text-[var(--qn-foreground-subtle)]">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="text-base">No schedules yet. Automate your workflow optimizations.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                </>
                )}
              </motion.div>
            )}

            {activeTab === "team" && (
              <motion.div
                key="team"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {teams.length === 0 ? (
                  <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                    <CardContent className="py-16 text-center">
                      <Users className="w-16 h-16 mx-auto mb-4 text-[var(--qn-foreground-subtle)] opacity-50" />
                      <h3 className="text-xl font-outfit font-semibold mb-2">No Teams Yet</h3>
                      <p className="text-[var(--qn-foreground-muted)] mb-6 max-w-md mx-auto">
                        Create a team to share workflows and collaborate on optimizations with your colleagues.
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <motion.div {...hoverScale}>
                          <Button onClick={() => setCreateTeamDialogOpen(true)} className="btn-primary" data-testid="create-team-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Team
                          </Button>
                        </motion.div>
                        <motion.div {...hoverScale}>
                          <Button onClick={() => setJoinDialogOpen(true)} variant="outline" className="border-[var(--qn-border)]" data-testid="join-team-btn">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Join Team
                          </Button>
                        </motion.div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {/* Team header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {teams.length > 1 && teams.map((t) => (
                          <motion.div key={t.team_id} {...hoverScale}>
                            <Button
                              variant={selectedTeam?.team_id === t.team_id ? "secondary" : "outline"}
                              size="sm"
                              onClick={() => loadTeam(t.team_id)}
                              className="border-[var(--qn-border)]"
                            >
                              {t.name}
                            </Button>
                          </motion.div>
                        ))}
                        {teams.length === 1 && (
                          <h3 className="text-xl font-outfit font-semibold">{selectedTeam?.name}</h3>
                        )}
                        {/* Live presence indicators */}
                        {teamPresence.length > 0 && (
                          <div className="flex items-center gap-1 ml-2">
                            {teamPresence.filter(p => p.user_id !== user?.user_id).slice(0, 5).map((p, i) => (
                              <motion.div
                                key={p.user_id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="relative"
                                title={`${p.name} is online`}
                              >
                                <div
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-[var(--qn-background)]"
                                  style={{ backgroundColor: p.color || '#06b6d4' }}
                                >
                                  {p.name?.charAt(0)}
                                </div>
                                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[var(--qn-success)] rounded-full border-2 border-[var(--qn-background)]" />
                              </motion.div>
                            ))}
                            {teamPresence.filter(p => p.user_id !== user?.user_id).length > 5 && (
                              <div className="w-7 h-7 rounded-full bg-[var(--qn-surface)] flex items-center justify-center text-[10px] text-[var(--qn-foreground-subtle)]">
                                +{teamPresence.filter(p => p.user_id !== user?.user_id).length - 5}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <motion.div {...hoverScale}>
                          <Button onClick={() => setEmailInviteDialogOpen(true)} variant="outline" size="sm" className="border-[var(--qn-border)]" data-testid="email-invite-btn">
                            <Mail className="w-4 h-4 mr-2" />
                            Email Invite
                          </Button>
                        </motion.div>
                        <motion.div {...hoverScale}>
                          <Button onClick={handleCreateInvite} variant="outline" size="sm" className="border-[var(--qn-border)]" data-testid="invite-member-btn">
                            <Link2 className="w-4 h-4 mr-2" />
                            Invite Link
                          </Button>
                        </motion.div>
                        <motion.div {...hoverScale}>
                          <Button onClick={() => setCreateTeamDialogOpen(true)} variant="outline" size="sm" className="border-[var(--qn-border)]">
                            <Plus className="w-4 h-4 mr-2" />
                            New Team
                          </Button>
                        </motion.div>
                        <motion.div {...hoverScale}>
                          <Button onClick={() => setJoinDialogOpen(true)} variant="outline" size="sm" className="border-[var(--qn-border)]">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Join
                          </Button>
                        </motion.div>
                      </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* Members */}
                      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                        <CardHeader>
                          <CardTitle className="font-outfit text-lg flex items-center gap-2">
                            <Users className="w-5 h-5 text-[var(--qn-primary)]" />
                            Members ({teamMembers.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-3">
                            {teamMembers.map((member) => (
                              <motion.div
                                key={member.user_id}
                                variants={staggerItem}
                                className="flex items-center justify-between"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center text-sm font-medium text-white">
                                    {member.name?.charAt(0) || "?"}
                                  </div>
                                  <div>
                                    <div className="text-base font-medium">{member.name}</div>
                                    <div className="text-sm text-[var(--qn-foreground-subtle)]">{member.email}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-sm ${
                                    member.role === 'owner' ? 'bg-amber-500/15 text-amber-500' :
                                    member.role === 'admin' ? 'bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)]' :
                                    'bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]'
                                  }`}>
                                    {member.role === 'owner' && <Crown className="w-3 h-3 mr-1" />}
                                    {member.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                                    {member.role}
                                  </Badge>
                                  {selectedTeam?.your_role === 'owner' && member.role !== 'owner' && (
                                    <Button
                                      variant="ghost" size="icon" className="h-6 w-6 text-red-400"
                                      onClick={() => handleRemoveMember(member.user_id)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        </CardContent>
                      </Card>

                      {/* Team Workflows */}
                      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] lg:col-span-2">
                        <CardHeader>
                          <CardTitle className="font-outfit text-lg flex items-center gap-2">
                            <Workflow className="w-5 h-5 text-[var(--qn-primary)]" />
                            Shared Workflows ({teamWorkflows.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {teamWorkflows.length > 0 ? (
                            <div className="space-y-3">
                              {teamWorkflows.slice(0, 8).map((wf) => (
                                <motion.div
                                  key={wf.workflow_id}
                                  whileHover={{ scale: 1.01 }}
                                  className="card-dark rounded-lg p-3 flex items-center justify-between"
                                >
                                  <div>
                                    <div className="font-medium text-base">{wf.name}</div>
                                    <div className="text-sm text-[var(--qn-foreground-subtle)]">
                                      by {wf.owner_name} · {wf.tasks?.length || 0} tasks
                                    </div>
                                  </div>
                                  <Badge className="bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] text-sm">
                                    v{wf.version || 1}
                                  </Badge>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-[var(--qn-foreground-subtle)]">
                              No shared workflows yet
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Team Activity */}
                    {teamActivity.length > 0 && (
                      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                        <CardHeader>
                          <CardTitle className="font-outfit text-lg flex items-center gap-2">
                            <Activity className="w-5 h-5 text-[var(--qn-success)]" />
                            Recent Activity
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {teamActivity.slice(0, 5).map((act, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className="flex items-center justify-between text-base"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-7 h-7 rounded-full bg-[var(--qn-primary-muted)] flex items-center justify-center text-xs text-[var(--qn-primary)]">
                                    {act.user_name?.charAt(0)}
                                  </div>
                                  <span className="text-[var(--qn-foreground-muted)]">
                                    {act.user_name} optimized <span className="font-mono text-[var(--qn-primary)]">{act.workflow_id}</span>
                                  </span>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-[var(--qn-success)] font-mono">{act.cost_reduction_percent}%</span>
                                  <Badge variant="outline" className="text-sm border-[var(--qn-border)]">{act.solver_type}</Badge>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
                {/* Real-Time Collaboration */}
                {selectedTeam && (
                  <div className="mt-6">
                    <CollaborationPanel teamId={selectedTeam.team_id} />
                  </div>
                )}
              </motion.div>
            )}



            {activeTab === "examples" && (
              <motion.div
                key="examples"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <CodeExamplesTab />
              </motion.div>
            )}
            {activeTab === "docs" && (
              <motion.div
                key="docs"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <DocsTab />
              </motion.div>
            )}
            {activeTab === "team-mgmt" && (
              <motion.div key="team-mgmt" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <TeamManagementTab />
              </motion.div>
            )}
            {activeTab === "usage" && (
              <motion.div key="usage" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <UsageDashboardTab />
              </motion.div>
            )}
            {activeTab === "billing" && (
              <motion.div key="billing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <BillingTab />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* All Dashboard Dialogs */}
        <DashboardDialogs
          deleteDialogOpen={deleteDialogOpen} setDeleteDialogOpen={setDeleteDialogOpen}
          workflowToDelete={workflowToDelete} handleDeleteWorkflow={handleDeleteWorkflow}
          versionsDialogOpen={versionsDialogOpen} setVersionsDialogOpen={setVersionsDialogOpen}
          versions={versions} handleRestoreVersion={handleRestoreVersion}
          createTeamDialogOpen={createTeamDialogOpen} setCreateTeamDialogOpen={setCreateTeamDialogOpen}
          newTeamName={newTeamName} setNewTeamName={setNewTeamName} handleCreateTeam={handleCreateTeam}
          inviteDialogOpen={inviteDialogOpen} setInviteDialogOpen={setInviteDialogOpen}
          inviteLink={inviteLink} copyToClipboard={copyToClipboard}
          joinDialogOpen={joinDialogOpen} setJoinDialogOpen={setJoinDialogOpen}
          joinCode={joinCode} setJoinCode={setJoinCode} handleJoinTeam={handleJoinTeam}
          compareDialogOpen={compareDialogOpen} setCompareDialogOpen={setCompareDialogOpen}
          compareSelected={compareSelected} toggleCompareSelection={toggleCompareSelection}
          optimizations={optimizations} handleCompare={handleCompare} comparing={comparing}
          compareResult={compareResult} setCompareResult={setCompareResult} setCompareSelected={setCompareSelected}
          emailInviteDialogOpen={emailInviteDialogOpen} setEmailInviteDialogOpen={setEmailInviteDialogOpen}
          inviteEmail={inviteEmail} setInviteEmail={setInviteEmail} handleEmailInvite={handleEmailInvite}
          shareDialogOpen={shareDialogOpen} setShareDialogOpen={setShareDialogOpen} shareUrl={shareUrl}
          scheduleDialogOpen={scheduleDialogOpen} setScheduleDialogOpen={setScheduleDialogOpen}
          workflows={workflows}
          newScheduleWorkflow={newScheduleWorkflow} setNewScheduleWorkflow={setNewScheduleWorkflow}
          newScheduleCron={newScheduleCron} setNewScheduleCron={setNewScheduleCron}
          newScheduleSuper={newScheduleSuper} setNewScheduleSuper={setNewScheduleSuper}
          handleCreateSchedule={handleCreateSchedule}
        />
      </div>
    </PageTransition>
  );
}
